import { OrderKind, QuoteAndPost, WRAPPED_NATIVE_CURRENCIES } from '@cowprotocol/cow-sdk';
import { BigNumber } from 'bignumber.js';
import { getEthersProvider } from 'src/libs/web3-data-provider/adapters/EthersAdapter';
import { CoWProtocolPricesService } from 'src/services/CoWProtocolPricesService';
import { getErrorTextFromError, TxAction } from 'src/ui-config/errorMapping';
import { wagmiConfig } from 'src/ui-config/wagmiConfig';

import { COW_PARTNER_FEE, isChainIdSupportedByCoWProtocol } from '../../constants/cow.constants';
import { isNativeToken } from '../../helpers/cow';
import { CowProtocolRatesType, ProviderRatesParams, SwapProvider } from '../../types';
import { getAppDataForQuote } from './adapters.helpers';
import { getCowTradingSdkByChainIdAndAppCode } from './env.helpers';
import { getSlippageSuggestion } from './orders.helpers';

export async function getCowProtocolSellRates({
  chainId,
  amount,
  srcToken,
  srcDecimals,
  destToken,
  destDecimals,
  user,
  swapType,
  inputSymbol,
  outputSymbol,
  appCode,
  market,
  setError,
  side = 'sell',
  invertedQuoteRoute = false,
}: ProviderRatesParams): Promise<CowProtocolRatesType> {
  const tradingSdk = await getCowTradingSdkByChainIdAndAppCode(chainId, appCode);
  const cowProtocolPricesService = new CoWProtocolPricesService();
  let orderBookQuote: QuoteAndPost | undefined;
  let srcTokenPriceUsd: string | undefined;
  let destTokenPriceUsd: string | undefined;
  try {
    if (!isChainIdSupportedByCoWProtocol(chainId)) {
      throw new Error('Chain not supported by CowProtocol');
    }

    // If srcToken is native, we need to use the wrapped token for the quote
    let srcTokenWrapped = srcToken;
    if (isNativeToken(srcToken)) {
      srcTokenWrapped = WRAPPED_NATIVE_CURRENCIES[chainId].address;
    }

    let destTokenWrapped = destToken;
    if (isNativeToken(destToken)) {
      destTokenWrapped = WRAPPED_NATIVE_CURRENCIES[chainId].address;
    }

    const provider = await getEthersProvider(wagmiConfig, { chainId });
    const signer = provider?.getSigner();

    if (!inputSymbol || !outputSymbol) {
      throw new Error('No input or output symbol provided');
    }

    [orderBookQuote, srcTokenPriceUsd, destTokenPriceUsd] = await Promise.all([
      tradingSdk
        .getQuote(
          {
            owner: user as `0x${string}`,
            kind: side === 'buy' ? OrderKind.BUY : OrderKind.SELL,
            amount,
            sellToken: srcTokenWrapped,
            sellTokenDecimals: srcDecimals,
            buyToken: destTokenWrapped,
            buyTokenDecimals: destDecimals,
            signer,
            appCode: appCode,
            partnerFee: COW_PARTNER_FEE(inputSymbol, outputSymbol, swapType, market),
          },
          {
            // Price Quality is set to OPTIMAL by default
            appData: await getAppDataForQuote({
              user,
              type: swapType,
              chainId,
              amount,
              srcToken,
              srcDecimals,
              destToken,
              destDecimals,
            }),
            getSlippageSuggestion,
          }
        )
        .catch((cowError) => {
          console.error(cowError);
          const errorMessage =
            cowError?.body?.errorType ||
            cowError?.body?.description ||
            cowError?.message ||
            'Failed to get quote from CoW Protocol';
          throw new Error(errorMessage);
        }),
      cowProtocolPricesService.getTokenUsdPrice(chainId, srcTokenWrapped),
      cowProtocolPricesService.getTokenUsdPrice(chainId, destTokenWrapped),
    ]);

    if (!srcTokenPriceUsd || !destTokenPriceUsd) {
      console.error('No price found for token');
      const error = getErrorTextFromError(
        new Error('No price found for token, please try another token'),
        TxAction.MAIN_ACTION,
        true
      );
      setError?.(error);
      console.error(error);
      throw new Error('No price found for token, please try another token');
    }
  } catch (error) {
    console.error('generate error', error);
    setError?.({
      error,
      blocking: true,
      actionBlocked: true,
      rawError: error,
      txAction: TxAction.MAIN_ACTION,
    });

    throw error;
  }

  if (!orderBookQuote.quoteResults.suggestedSlippageBps) {
    console.error('No suggested slippage found');
    const error = getErrorTextFromError(
      new Error('No suggested slippage found'),
      TxAction.MAIN_ACTION,
      true
    );
    setError?.(error);
    console.error(error);
    throw new Error('No suggested slippage found');
  }

  if (!orderBookQuote.quoteResults.amountsAndCosts.afterPartnerFees.buyAmount) {
    console.error('No buy amount found');
    const error = getErrorTextFromError(
      new Error('No buy amount found'),
      TxAction.MAIN_ACTION,
      true
    );
    setError?.(error);
    console.error(error);
    throw new Error('No buy amount found');
  }

  let suggestedSlippage = (orderBookQuote.quoteResults.suggestedSlippageBps ?? 100) / 100; // E.g. 100 bps -> 1% 100 / 100 = 1

  if (isNativeToken(srcToken)) {
    // Recommended by CoW for potential reimbursments
    if (chainId == 1 && suggestedSlippage < 2) {
      suggestedSlippage = 2;
    } else if (chainId != 1 && suggestedSlippage < 0.5) {
      suggestedSlippage = 0.5;
    }
  }

  if (invertedQuoteRoute) {
    // Calculate Amounts
    const srcSpotAmount =
      orderBookQuote.quoteResults.amountsAndCosts.beforeNetworkCosts.buyAmount.toString();
    const srcSpotUSD = BigNumber(destTokenPriceUsd)
      .multipliedBy(BigNumber(srcSpotAmount).dividedBy(10 ** destDecimals))
      .toString();
    const destSpotAmount =
      orderBookQuote.quoteResults.amountsAndCosts.beforeNetworkCosts.sellAmount.toString();
    const destSpotUSD = BigNumber(srcTokenPriceUsd)
      .multipliedBy(BigNumber(destSpotAmount).dividedBy(10 ** srcDecimals))
      .toString();
    const afterFeesAmount =
      orderBookQuote.quoteResults.amountsAndCosts.afterPartnerFees.sellAmount.toString();
    const afterFeesUSD = BigNumber(srcTokenPriceUsd)
      .multipliedBy(BigNumber(afterFeesAmount).dividedBy(10 ** srcDecimals))
      .toString();

    return {
      srcToken: destToken,
      srcSpotUSD,
      srcSpotAmount: srcSpotAmount,
      srcDecimals: destDecimals,
      destToken: srcToken,
      destSpotAmount,
      destSpotUSD,
      afterFeesUSD,
      afterFeesAmount,
      destDecimals: srcDecimals,
      orderBookQuote,
      provider: SwapProvider.COW_PROTOCOL,
      order: orderBookQuote.quoteResults.orderToSign,
      quoteId: orderBookQuote.quoteResults.quoteResponse.id,
      suggestedSlippage,
      amountAndCosts: orderBookQuote.quoteResults.amountsAndCosts,
      srcTokenPriceUsd: Number(destTokenPriceUsd),
      destTokenPriceUsd: Number(srcTokenPriceUsd),
    };
  } else {
    // Calculate Amounts
    const srcSpotAmount =
      orderBookQuote.quoteResults.amountsAndCosts.beforeNetworkCosts.sellAmount.toString();
    const srcSpotUSD = BigNumber(srcTokenPriceUsd)
      .multipliedBy(BigNumber(srcSpotAmount).dividedBy(10 ** srcDecimals))
      .toString();
    const destSpotAmount =
      orderBookQuote.quoteResults.amountsAndCosts.beforeNetworkCosts.buyAmount.toString();
    const destSpotUSD = BigNumber(destTokenPriceUsd)
      .multipliedBy(BigNumber(destSpotAmount).dividedBy(10 ** destDecimals))
      .toString();
    const afterFeesAmount =
      orderBookQuote.quoteResults.amountsAndCosts.afterPartnerFees.buyAmount.toString();
    const afterFeesUSD = BigNumber(destTokenPriceUsd)
      .multipliedBy(BigNumber(afterFeesAmount).dividedBy(10 ** destDecimals))
      .toString();

    return {
      srcToken,
      srcSpotUSD,
      srcSpotAmount,
      srcDecimals,
      destToken,
      destSpotAmount,
      destSpotUSD,
      afterFeesUSD,
      afterFeesAmount,
      destDecimals,
      orderBookQuote,
      provider: SwapProvider.COW_PROTOCOL,
      order: orderBookQuote.quoteResults.orderToSign,
      quoteId: orderBookQuote.quoteResults.quoteResponse.id,
      suggestedSlippage,
      amountAndCosts: orderBookQuote.quoteResults.amountsAndCosts,
      srcTokenPriceUsd: Number(srcTokenPriceUsd),
      destTokenPriceUsd: Number(destTokenPriceUsd),
    };
  }
}
